/* =============================================================================
 * A QUIÉN LE SALE CADA ACTUALIZACIÓN
 * -----------------------------------------------------------------------------
 * Piezas puras —sin React ni Firebase— que deciden, a partir de la lista de
 * `src/novedades.js`:
 *   · qué entradas le APLICAN a una cuenta (su rol y los módulos que usa): es lo
 *     que se enseña en "Actualizaciones" del perfil, de la más reciente a la más
 *     antigua;
 *   · cuáles tiene PENDIENTES para el aviso flotante: le aplican, no las ha visto
 *     en ningún aparato, y su cuenta ya existía el día de la entrada;
 *   · el aviso: UNO solo, con la más reciente, y cuántas más hay.
 *
 * CommonJS, exportado una a una y sin desestructurar en bucles: es la forma que
 * entienden igual la aplicación (webpack) y las pruebas (Node). Ver los demás
 * archivos de src/logica.
 * ========================================================================== */

const ROLES_NOVEDAD = ["patron", "trabajador"];
// Los módulos que una entrada puede exigir. Uno que no esté aquí NO se enseña a
// nadie: anunciar algo de un módulo que la plataforma no sabe comprobar sería
// avisar a quien no lo usa.
const MODULOS_NOVEDAD = ["flotilla"];

function _esFechaNovedad(f) {
  return typeof f === "string" && /^\d{4}-\d{2}-\d{2}$/.test(f);
}
// El primer instante del día SIGUIENTE a la fecha de la entrada, en hora local.
// Una cuenta creada antes de ese instante —el mismo día de la entrada, o antes—
// sí ve el aviso; una creada después, no.
function finDelDiaNovedadMs(fecha) {
  if (!_esFechaNovedad(fecha)) return 0;
  const p = fecha.split("-");
  return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]) + 1).getTime();
}

// ¿Esta entrada le aplica a esta cuenta? `cuenta` = { rol, tieneFlotilla }.
function novedadAplica(e, cuenta) {
  if (!e || !cuenta) return false;
  if (ROLES_NOVEDAD.indexOf(cuenta.rol) < 0 || e.para !== cuenta.rol) return false;
  if (e.requiere === null || e.requiere === undefined || e.requiere === "") return true;
  if (MODULOS_NOVEDAD.indexOf(e.requiere) < 0) return false;
  if (e.requiere === "flotilla") return cuenta.tieneFlotilla === true;
  return false;
}

// TODAS las que le aplican, de la más reciente a la más antigua. Con la misma
// fecha, la que va después en el archivo se considera más reciente.
function novedadesDeLaCuenta(lista, cuenta) {
  const conIndice = (lista || []).map((e, i) => ({ e: e, i: i })).filter((x) => novedadAplica(x.e, cuenta));
  conIndice.sort((a, b) => (String(b.e.fecha).localeCompare(String(a.e.fecha))) || (b.i - a.i));
  return conIndice.map((x) => x.e);
}

// Las PENDIENTES del aviso flotante. `vistas` = ids ya vistos (en cualquier
// aparato); `altaMs` = cuándo se creó la cuenta. Sin fecha de alta conocida, se
// trata como cuenta antigua: es el lado que avisa de más, no el que se calla.
function novedadesPendientes(lista, cuenta, vistas, altaMs) {
  const v = Array.isArray(vistas) ? vistas : [];
  return novedadesDeLaCuenta(lista, cuenta).filter((e) =>
    v.indexOf(e.id) < 0 && (!(Number(altaMs) > 0) || Number(altaMs) < finDelDiaNovedadMs(e.fecha)));
}

// UN SOLO AVISO: la más reciente, y cuántas más hay detrás.
function avisoDeNovedades(lista, cuenta, vistas, altaMs) {
  const pendientes = novedadesPendientes(lista, cuenta, vistas, altaMs);
  return { principal: pendientes[0] || null, pendientes: pendientes, mas: Math.max(0, pendientes.length - 1) };
}

// "5 oct 2026", armado número a número: `new Date("2026-10-05")` se interpreta
// en UTC y en México retrocede un día.
const _MESES_NOVEDAD = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
function fechaNovedadTexto(fecha) {
  if (!_esFechaNovedad(fecha)) return "";
  const p = fecha.split("-");
  return Number(p[2]) + " " + _MESES_NOVEDAD[Number(p[1]) - 1] + " " + p[0];
}

// La lista bien formada: ids únicos, fecha válida, rol y módulo conocidos, y
// texto en todo. Devuelve los problemas; vacío si está bien.
function problemasDeNovedades(lista) {
  const problemas = [];
  const ids = {};
  (lista || []).forEach((e, i) => {
    const q = "entrada " + i + (e && e.id ? " (" + e.id + ")" : "");
    if (!e || typeof e.id !== "string" || !e.id.trim()) { problemas.push(q + ": sin id"); return; }
    if (ids[e.id]) problemas.push(q + ": id repetido");
    ids[e.id] = true;
    if (!_esFechaNovedad(e.fecha)) problemas.push(q + ": fecha inválida");
    if (ROLES_NOVEDAD.indexOf(e.para) < 0) problemas.push(q + ": para desconocido");
    if (!(e.requiere === null || MODULOS_NOVEDAD.indexOf(e.requiere) >= 0)) problemas.push(q + ": requiere desconocido");
    if (!String(e.titulo || "").trim()) problemas.push(q + ": sin título");
    if (!String(e.resumen || "").trim()) problemas.push(q + ": sin resumen");
    if (!Array.isArray(e.puntos) || !e.puntos.length || e.puntos.some((p) => !String(p || "").trim())) problemas.push(q + ": puntos vacíos");
  });
  return problemas;
}

exports.ROLES_NOVEDAD = ROLES_NOVEDAD;
exports.MODULOS_NOVEDAD = MODULOS_NOVEDAD;
exports.finDelDiaNovedadMs = finDelDiaNovedadMs;
exports.novedadAplica = novedadAplica;
exports.novedadesDeLaCuenta = novedadesDeLaCuenta;
exports.novedadesPendientes = novedadesPendientes;
exports.avisoDeNovedades = avisoDeNovedades;
exports.fechaNovedadTexto = fechaNovedadTexto;
exports.problemasDeNovedades = problemasDeNovedades;
